// #522 — every class list shows the class NUMBER and sorts by it numerically.
// The class number (`classNumber`) is stamped from the FVD day sheet on
// reconcile; `className` is the class name.
import {
  DERIVED_CLASS_STATUSES,
  DERIVED_RIDE_STATUSES,
  RIDE_STATUSES,
  isClassComplete,
} from "score-common";
// #1805 — scheduledTime is an FVD stamp, not a genuine UTC instant (#1811):
// FVD's Access DB holds a naive venue wall-clock time, ADODB on `fvdts`
// (America/Los_Angeles) parses it as box-local, and foxbox reads it back
// with UTC getters. Rendering it back through the SAME zone is the exact
// inverse of that mangling, which is why times below have always displayed
// correctly. FVD_STAMP_TIMEZONE is common-fe's shared name for that fixed
// zone (#1811) - importing it here retires this file's own duplicate
// constant (was `VENUE_TIME_ZONE = "America/Los_Angeles"`) without changing
// the value or any rendered time. Do NOT swap this for a show's real
// venueTimezone: that's the opposite half of the #1811 split and moves every
// ride time by the fvdts/venue offset (3h at an Eastern show like WEC).
import { FVD_STAMP_TIMEZONE } from "common-fe/venueTime";

// "12. Training Level Test 1" when a class number is present, else just the name.
export function classNumberLabel(c) {
  const n = c?.classNumber;
  const name = c?.className || c?.id || "";
  return n != null && `${n}`.trim() !== "" ? `${n}. ${name}` : name;
}

// card #1664 — resolve a catalog test def's display NAME off a def id: the
// hydrated GET /catalog cache (`catalogDefsById`, useCatalogDefs) is
// preferred, falling back to `fallbackName` when passed.
//
// #1665 — was a demotion in priority order (catalog-authority.md section 5)
// down to the build-time bundled catalog (score-common/catalog's
// getCatalogTestById) for a scribe kiosk that had never hydrated catalogDefs
// (cold start, no network since install) or whose cache didn't carry this
// particular id. That bundle is retired. The judge's independent read of
// useScoreSheet.js (apps/scribe-fe/src/hooks/useScoreSheet.js:262-280) found
// scoring itself (ScoringForm.jsx) never touched score-common/catalog at
// all — it reads the scored test from the SEPARATE `tests` IndexedDB store /
// GET /test, always present in exactly the states a scribe can score. So the
// caller-supplied `fallbackName` (a ride's own `test?.name ?? test?.testCode`
// from that store, where a caller has one -- #1878: score-api's Test entity
// now also carries the catalog's clean `.name` alongside `testCode`
// (apps/score-api/src/lib/catalog-enrich.js's catalogTestFields()), so the
// fallback itself prefers the catalog name over FVD's raw testCode, same
// order as RideList's manual-add picker)
// is strictly BETTER than the retired bundle ever was: it closes the exact
// window the bundle fallback existed for (catalogDefs hydration failed but
// `tests` hydration succeeded), with no second, separately-synced copy of
// test data to go stale. `catalogDefsById` and `fallbackName` are both
// optional; with neither, this returns null.
export function resolveCatalogTestName(testId, catalogDefsById, fallbackName) {
  if (testId == null) return null;
  return catalogDefsById?.[testId]?.name ?? fallbackName ?? null;
}

// #896 — the full test NAME for a class-menu card, resolved off the
// reconcile-stamped Class.testId convenience hint against the catalog (table
// cache only — see resolveCatalogTestName; no per-ride `test` row exists at
// class level to fall back to). Class.testId is a non-authoritative HINT for
// a homogeneous class (#435) — a Test-of-Choice class carrying a different
// test per ride has no single testId here, so this returns null and the
// card simply omits the line rather than guessing at one ride's test. The
// scribe still sees the authoritative per-ride test once inside the class
// (RideList/ScoringForm, #915's getRideTestName, #1664's rideTestName) —
// this is only the class-menu preview.
export function classTestName(c, catalogDefsById) {
  return resolveCatalogTestName(c?.testId, catalogDefsById);
}

// #915 / #1664 — the full test name a RIDE is riding, resolved per-ride off
// ride.testId (never per-class: a Test-of-Choice class lets different rides
// carry different testIds, see classTestName above). Table cache preferred,
// caller-supplied fallback (typically the ride's own scored `test?.name`,
// see resolveCatalogTestName) second.
export function rideTestName(ride, catalogDefsById, fallbackName) {
  return resolveCatalogTestName(ride?.testId, catalogDefsById, fallbackName);
}

// #883 — FVD gives a ride-out/split section a FRACTIONAL sngClassID (base 223
// -> split 223.1000, labeled "223RO" in FVD Desktop), but the ingested
// classNumber/className text (chrText/chrName) is carried verbatim from the
// SAME FVD row shape and is typically IDENTICAL between base and split — so
// they render as two indistinguishable "223. <name>" entries in every class
// list/search. A scribe resolving an off-daysheet CNO to a class (the
// manual-add flow) can silently land on the split (CDS prod, CNO 462) when
// the real ride belongs on the base class. Detect the split off the class
// `id`, which always carries the raw sngClassID
// (`...sngClassID-223.1000...`) even when the display text doesn't.
const SNG_CLASS_ID_RE = /sngClassID-([0-9]+(?:\.[0-9]+)?)(?:[:.]|$)/;

export function isSplitClass(c) {
  const m = SNG_CLASS_ID_RE.exec(c?.id || "");
  if (!m) return false;
  const n = Number(m[1]);
  return Number.isFinite(n) && !Number.isInteger(n);
}

const leadingInt = (v) => {
  const m = /^\s*(\d+)/.exec(v == null ? "" : String(v));
  return m ? parseInt(m[1], 10) : NaN;
};

// Numeric-first comparator: classes with a numeric class number sort ascending
// by it (tie-break on the raw string, e.g. "12" before "12A"); classes with no
// number sort after them, keeping their incoming order (returns 0 → stable).
// #883 — when two classes carry the SAME number (base + its split/RO
// section), the base sorts first so it's never buried under an
// identically-labeled split.
export function compareByClassNumber(a, b) {
  const na = leadingInt(a?.classNumber);
  const nb = leadingInt(b?.classNumber);
  const aOk = !Number.isNaN(na);
  const bOk = !Number.isNaN(nb);
  if (aOk && bOk) {
    if (na !== nb) return na - nb;
    const aSplit = isSplitClass(a) ? 1 : 0;
    const bSplit = isSplitClass(b) ? 1 : 0;
    if (aSplit !== bSplit) return aSplit - bSplit;
    return String(a.classNumber).localeCompare(String(b.classNumber));
  }
  if (aOk) return -1;
  if (bOk) return 1;
  return 0;
}

// Stable numeric sort of a classes array (never mutates the input).
export function sortByClassNumber(classes) {
  return (classes ?? []).slice().sort(compareByClassNumber);
}

// #632 — order strictly by WHEN things run, soonest first. `scheduledTime` is
// an ISO string (FVD stamps naive-local as Z, #591/#592) so a plain string
// compare IS a time compare — never parse a Date or shift timezones here.
// Missing/blank scheduledTime sorts LAST (nulls last), never first.
export function compareByScheduledTime(a, b) {
  const at = (a?.scheduledTime || "").trim();
  const bt = (b?.scheduledTime || "").trim();
  if (!at && !bt) return 0;
  if (!at) return 1;
  if (!bt) return -1;
  return at.localeCompare(bt);
}

// Stable sort by scheduledTime ASC, nulls last; `tieBreak(a, b)` (e.g.
// compareByClassNumber, or a rideNumber diff) breaks ties on equal/missing
// times so callers reuse one comparator for both classes and rides (#632).
export function sortByScheduledTime(items, tieBreak) {
  return (items ?? [])
    .slice()
    .sort((a, b) => compareByScheduledTime(a, b) || (tieBreak ? tieBreak(a, b) : 0));
}

// #636 / #856 — a ride is no longer "up next" once it's scored
// (COMPLETED/FINAL), fully confirmed (CONFIRMED — every expected judge has
// confirmed marks, so the horse has already left the ring even if paperwork
// like comments/signatures is still in flight), withdrawn (SCRATCHED), or
// already in the ring (IN_PROGRESS). The in-ring one matters: "next" has to
// mean the ride AFTER the one being ridden, or a scribe mid-test sees the
// horse in front of them announced as what's coming. Without CONFIRMED here,
// a confirmed-but-unsigned ride still read as "upcoming" and could get
// re-announced as next. Statuses come from score-common, same as RideList —
// no parallel rule.
const NOT_UPCOMING_STATUSES = new Set([
  DERIVED_RIDE_STATUSES.COMPLETED,
  DERIVED_RIDE_STATUSES.CONFIRMED,
  DERIVED_RIDE_STATUSES.FINAL,
  DERIVED_RIDE_STATUSES.IN_PROGRESS,
  // #768 — the same "already in the ring" reasoning above applies the
  // moment C rings the Bell, before "In Ring" ever flips it to IN_PROGRESS:
  // the horse in front of the scribe is not "coming up next".
  DERIVED_RIDE_STATUSES.ENTERING_RING,
  // #3088 — a ride sent to the review queue is closed to the scribe, so it is
  // not "coming up next" either.
  DERIVED_RIDE_STATUSES.IN_REVIEW,
  // #3099 - an ended ride with unsigned marks was already ridden.
  DERIVED_RIDE_STATUSES.NEEDS_SIGNING,
  RIDE_STATUSES.SCRATCHED,
]);

// #636 — the next ride still to come, off the SAME time order RideList renders
// (sortByScheduledTime + rideNumber tie-break). `rides` must already carry a
// derived `status` (e.g. from useRidesForClass); this never re-derives or
// re-queries anything. Returns null when nothing is left.
export function nextUpcomingRide(rides) {
  const ordered = sortByScheduledTime(
    rides,
    (a, b) => (a.rideNumber ?? 0) - (b.rideNumber ?? 0),
  );
  return ordered.find((r) => !NOT_UPCOMING_STATUSES.has(r.status)) ?? null;
}

// #647 / #856 — the scribe's next assigned class, off the SAME order
// useClassesForShow already returns (assignment-filtered + scheduledTime-
// sorted, #627/#632): the first one that isn't already done. Returns null
// when nothing is left.
//
// `Class.status` is DEAD — never written by reconcile or anywhere else
// (classSchema declares no `status` field), so a class is never actually
// "done" by that reading and the old status-based filter always fell through
// to classes[0], permanently stuck on the first assigned class. Completion is
// a property of its RIDES, not the class row: isClassComplete (score-common)
// is true once every ride is FINAL or SCRATCHED. `ridesByClassId` maps each
// candidate class's id to its rides (see useRidesByClassId); a class with no
// entry yet (rides not loaded) is treated as NOT complete rather than
// skipped, so the standby screen never jumps ahead of data it hasn't seen.
export function nextAssignedClass(classes, ridesByClassId = {}) {
  return (
    (classes ?? []).find((c) => !isClassComplete(ridesByClassId?.[c?.id])) ?? null
  );
}

// #1557 — the next class still to RUN, off the same order useClassesForShow
// already returns (assignment-filtered + scheduledTime-sorted, #627/#632): the
// first one whose derived status is SCHEDULED, i.e. "no ride has started
// scoring yet" (deriveClassStatus, #1280) — exactly "a class with no scores in
// it". Distinct from nextAssignedClass above, which answers "what am I not
// finished with" (anything not COMPLETE, so it stays on a half-scored class);
// this answers "what hasn't begun".
//
// `statusesByClassId` is useClassStatuses' map. A class with no entry yet
// (statuses still loading, or an offline cold cache) is NOT treated as
// SCHEDULED — asserting "this hasn't started" about a class we know nothing
// about is the same false-negative that made the old next-ride chip a lie.
// Returns null when nothing is left, which the caller renders as an explicit
// empty state rather than a time.
export function nextScheduledClass(classes, statusesByClassId) {
  const statuses = statusesByClassId ?? {};
  return (
    (classes ?? []).find(
      (c) => statuses[c?.id] === DERIVED_CLASS_STATUSES.SCHEDULED,
    ) ?? null
  );
}

// #135 / #1702 — split a class list into what's still to score and what's
// done, so the scribe never has to scan past finished classes to reach
// anything actionable (bug report #135's screenshot: three Placed classes
// sitting ahead of the next Scheduled one). "Done" is COMPLETE or PLACED
// (deriveClassStatus, #1280) — the same two states STATUS_COLORS already
// renders a finished-looking chip for. `classes` must already be in the
// order the caller wants preserved (ClassList passes its scheduledTime-
// sorted visibleClasses, #632) — this only PARTITIONS, it never re-sorts, so
// each half keeps its incoming relative order (AC2: "next to score" stays on
// top of the active half).
//
// A class with no status entry yet (statuses still loading, or an offline
// cold cache) is treated as NOT done — the same conservative default
// nextScheduledClass/nextAssignedClass already use above: asserting "this is
// finished" about a class we know nothing about would risk burying it below
// the divider where a scribe wouldn't think to look for still-open work.
export function partitionClassesByDone(classes, statusesByClassId) {
  const statuses = statusesByClassId ?? {};
  const active = [];
  const done = [];
  for (const c of classes ?? []) {
    const status = statuses[c?.id];
    const isDone =
      status === DERIVED_CLASS_STATUSES.COMPLETE ||
      status === DERIVED_CLASS_STATUSES.PLACED;
    (isDone ? done : active).push(c);
  }
  return { active, done };
}

// #1557 — the time to SHOW for a class. `Class.scheduledTime` is sparse (only
// 105 of the 189 classes on the demo show carry one), so fall back to the
// class's earliest RIDE time — the same way announcer-fe already positions a
// class ("a class's position comes from its rides' times", orderOfGo.js).
// `earliestRideTimeByClassId` comes off useClassStatuses, which already holds
// the ride rows for the whole screen — never re-fetch or fan out per class to
// answer this. Returns null when neither is known, so the caller can render the
// class without inventing a time for it.
export function classEffectiveTime(c, earliestRideTimeByClassId = {}) {
  const own = (c?.scheduledTime || "").trim();
  if (own) return own;
  const fromRides = earliestRideTimeByClassId?.[c?.id];
  return fromRides ? String(fromRides) : null;
}

// #1557 / #688 / #805 — render a scheduled time in the VENUE's wall clock.
// #1811 corrected the belief this comment used to state ("scheduledTime is
// genuine UTC"): it is an FVD Access stamp, not a real instant. FVD's Access
// DB holds a naive venue wall-clock time, ADODB on `fvdts`
// (America/Los_Angeles) parses that as box-local, and foxbox reads it back
// with UTC getters — so "2026-08-04T16:52:00.000Z" is the 9:52 AM the
// printed day sheet shows only because rendering it back through the SAME
// fixed zone (FVD_STAMP_TIMEZONE) is the exact inverse of that mangling.
// Formatting it browser-local is only accidentally right on a kiosk
// physically sitting in that zone; formatting it as UTC, or through a show's
// real `venueTimezone`, prints the wrong wall clock — the #688 bug and the
// #1805 regression #1811 headed off, respectively. Same conversion as
// announcer-fe's formatTime and scoreadmin-fe's classDisplay.
//
// #1558 — this is now the ONE shared formatter for every class/ride time on
// screen (ClassList, RideList, SessionHeader, InitialSetup's next-class/
// next-ride block); each used to carry its own local `new
// Date(iso).toLocaleTimeString([], {...})` copy with no `timeZone`, so it
// rendered correctly only by accident, on a device physically sitting in
// America/Los_Angeles. `hour12` defaults to true (matches every existing
// caller's prior behavior, which never passed an explicit hour12 and got
// en-US's 12-hour default); InitialSetup is the one caller that wires it to
// the scribe's stored 12h/24h display preference (#710). Not delegated to
// common-fe's `formatFvdStampTime` because that helper has no `hour12`
// option; only the zone constant is shared (#1805).

export function formatVenueTime(iso, { hour12 = true } = {}) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: FVD_STAMP_TIMEZONE,
    hour12,
  });
}

// #910 / #1559 — the venue-local `YYYY-MM-DD` calendar day a timestamp falls
// on, via a genuine Intl timezone conversion (America/Los_Angeles, same
// FVD_STAMP_TIMEZONE as formatVenueTime above). `en-CA`'s default date format
// happens to be "YYYY-MM-DD", a convenient, dependency-free way to pull a
// sortable key out of Intl.
//
// #1559 — this used to be a naive string-slice of the first 10 characters of
// scheduledTime, on the belief that FVD stamps naive-local wall-clock times
// with a trailing "Z" (#590/#592/#634). That belief is stale: foxbox now
// emits genuine UTC (#805), so a class running from ~5:00 PM venue-local
// onward during PDT has a scheduledTime whose UTC calendar date has already
// rolled to tomorrow — the naive slice put it on the WRONG day, dropping it
// out of ClassList's default "Today only" view. Same conversion as
// announcer-fe's venueDayKey (apps/announcer-fe/src/utils/formatTime.js,
// #892) and report-defs' classDayKey
// (packages/report-defs/src/score/class-day.js, #689, already fixed), plus
// the same two input-shape guards as report-defs. announcer-fe's copy still
// lacks them, tracked as #1649 (https://trello.com/c/R2NeeiZe). Kept as its
// own copy here since scribe-fe doesn't share a util package with them:
//   1. require a date+time shape (`YYYY-MM-DDTHH:MM`) before parsing at all.
//      A date-ONLY string ("2026-08-04") parses as UTC MIDNIGHT, so
//      converting it straight to the venue zone reads back "2026-08-03" —
//      off by one. There's no way to recover the intended day from a
//      date-only value, so this returns null rather than guess.
//   2. append a "Z" when the string carries no zone marker. Per ECMA-262, an
//      unzoned datetime string ("2026-05-15T09:00:00") parses as
//      DEVICE-local, so the key would depend on the kiosk's own timezone —
//      scheduledTime always MEANS UTC (#805), zone marker or not.
// scoreadmin-fe's own copy (apps/scoreadmin-fe/src/util/classDisplay.js,
// #634) still carries the naive slice; left OUT OF SCOPE here because it's
// already tracked as its own card, #1623 (same wall-clock-truth workstream)
// — a different app, its own Day-picker surface (useShowRides.js/
// ReportsPage/ClassManager/PublishResults), and its own test/screenshot
// verification.
function formatVenueDay(d) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: FVD_STAMP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function venueDayKey(v) {
  if (!v) return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : formatVenueDay(v);
  }
  const raw = String(v);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return null;
  const iso = /[Zz]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : formatVenueDay(d);
}

export function classDayKey(c) {
  return venueDayKey(c?.scheduledTime);
}

// #910 — "today", in the SAME fixed FVD_STAMP_TIMEZONE classDayKey() above
// uses for scheduledTime (#688) — never the scribe device's own local/UTC
// day, and (#1805) deliberately NOT the show's real venueTimezone either.
// `now` here is a genuine instant, and #1813 established that a genuine
// instant belongs on the venue's real zone once one is threaded in (see
// announcer-fe's venueTodayKey) — but scribe-fe has no venueTimezone wiring
// into this file yet, and there is no urgency to add it (Jaybill,
// 2026-08-21 on #1805: these renderers are "correct by cancellation", not
// broken). Comparing "today" against classDayKey(scheduledTime) only lines
// up when both sides key off the SAME zone, so this intentionally stays on
// FVD_STAMP_TIMEZONE for now rather than silently drifting out of sync with
// the stamp-keyed day above. Wiring a real venueTimezone through here is
// follow-up work, not this card.
// #1559 — this is the SAME venueDayKey() conversion classDayKey() uses
// above, so "today" and a class's day can never disagree by construction
// (they used to be two different derivations — a naive slice for the class,
// a real timezone conversion for "now" — which is exactly what let an
// evening class silently fall off the default view). Accepts `now` so a
// caller (or test) can pin it.
export function venueTodayKey(now = new Date()) {
  return venueDayKey(now);
}
